/**
 * Pre-dental letter — interactive form for populating fields inline.
 */
import { SNOOTS_BG } from '@snootsvet/shared'

import { DatePicker } from '@/frontend/components/date-picker'
import { TimePicker } from '@/frontend/components/time-picker'
import { ClinicHeaderPreview } from '@/frontend/features/printing/documents/blocks/clinic-header'
import { DocumentPreview } from '@/frontend/features/printing/documents/blocks/document-preview'
import {
  AFTERCARE_TEXT,
  closingText,
  contactInfoText,
  defaultPreDentalLetterFields,
  FASTING_INSTRUCTIONS,
  formatArrivalTime,
  healthUpdatesText,
  introText,
  OPTIONAL_EXTRAS,
  PROCEDURE_INFO,
  type PreDentalLetterFields,
  PreDentalLetterPdf,
  pickUpText,
} from '@/frontend/features/printing/documents/pre-dental-letter.print'
import type { DocumentFormProps } from '@/frontend/features/printing/documents/types'
import { withDocumentEditor } from '@/frontend/features/printing/documents/with-document-editor'

function PreDentalLetterForm({
  context,
  value,
  onChange,
}: DocumentFormProps<PreDentalLetterFields>) {
  const { patient, owner, clinic } = context

  return (
    <DocumentPreview>
      <ClinicHeaderPreview clinic={clinic} />

      <p className="mt-4">Dear {owner.firstName},</p>

      <p className="mt-3">{introText(patient.name)}</p>

      <p className="mt-4 font-semibold">
        The dental procedure will take place at {clinic.address}.
      </p>

      <p className="mt-3 flex flex-wrap items-center gap-1">
        <span className="font-semibold">Please arrive at</span>
        <TimePicker
          className="inline-flex w-28"
          placeholder="time"
          value={value.arrivalTime || undefined}
          onChange={(t) => onChange({ ...value, arrivalTime: t })}
          min="07:00"
          max="12:00"
          snap={5}
        />
        <span className="font-semibold">on the</span>
        <DatePicker
          value={value.appointmentDate ?? undefined}
          onChange={(d) => onChange({ ...value, appointmentDate: d ?? null })}
          placeholder="date"
          className="w-48"
        />
        <span className="font-semibold">for your admit appointment.</span>
      </p>

      <h2 className="text-headline-md">What You Need to Know:</h2>

      <h3 className="text-headline-sm">1. Fasting Instructions</h3>
      <ul className="mt-1 list-disc pl-6">
        {FASTING_INSTRUCTIONS.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>

      <h3 className="text-headline-sm">2. What to Expect During the Procedure</h3>
      <ul className="mt-1 list-disc pl-6">
        {PROCEDURE_INFO.map((item) => (
          <li key={item.label}>
            <span className="font-semibold">{item.label}:</span> {item.text}
          </li>
        ))}
      </ul>

      <h3 className="text-headline-sm">3. Preparing for the Day</h3>
      <ul className="mt-1 list-disc pl-6">
        <li>
          <span className="font-semibold">Health Updates:</span> {healthUpdatesText(patient.name)}
        </li>
        <li>
          <span className="font-semibold">Contact Info:</span> {contactInfoText(owner.phone)}
        </li>
        <li>
          <span className="font-semibold">Arrival Time:</span> Don't forget to arrive at{' '}
          {value.arrivalTime ? formatArrivalTime(value.arrivalTime) : '______'} for the admission
          appointment.
        </li>
      </ul>

      <h3 className="text-headline-sm">4. Optional Extras</h3>
      <ul className="mt-1 list-disc pl-6">
        {OPTIONAL_EXTRAS.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>

      <h3 className="text-headline-sm">5. Post-Op Care</h3>
      <ul className="mt-1 list-disc pl-6">
        <li>
          <span className="font-semibold">Pick-Up Time:</span> {pickUpText(patient.name)}
        </li>
        <li>
          <span className="font-semibold">Aftercare:</span> {AFTERCARE_TEXT}
        </li>
      </ul>

      <p className="mt-4">{closingText(patient.name)}</p>

      <div className="mt-4">
        <p>Best wishes,</p>
        <p>The Snoots Team, North London</p>
      </div>
    </DocumentPreview>
  )
}

export const preDentalLetter = withDocumentEditor({
  id: 'pre-dental-letter',
  label: 'Pre-Dental Letter',
  // Dental procedures are carried out at the Bounds Green clinic.
  clinic: SNOOTS_BG,
  defaultFields: defaultPreDentalLetterFields,
  Preview: PreDentalLetterForm,
  Pdf: PreDentalLetterPdf,
})
